import { LANGUAGES } from './constants';
import { generateContentWithRetry, SYSTEM_INSTRUCTION_TRANSLATOR } from './gemini';

export interface CheckinTranslationDictionary {
  weekly_checkin: string;
  weekly_checkin_honesty_note: string;
  please_answer_all: string;
  additional_details: string;
  additional_details_placeholder: string;
  cancel: string;
  submit: string;
  processing: string;
  one_submission_per_week: string;
  submit_weekly_checkin: string;
  submission_window_closed: string;
  submission_window_closed_msg: string;
  submission_window_info: string;
  thank_you: string;
  submitted_msg: string;
  recent_checkins: string;
  no_checkins_yet: string;
  week_of: string;
  week_colon: string;
  show_original: string;
  see_translation: string;
  translate: string;
  translated_to: string;
  translating: string;

  // Questions
  q_study_stress: string;
  g_study_stress: string;
  q_understanding: string;
  g_understanding: string;
  q_physical_activity: string;
  g_physical_activity: string;
  q_screen_time: string;
  g_screen_time: string;
  q_daily_routine: string;
  g_daily_routine: string;
  q_social_emotional: string;
  g_social_emotional: string;
  q_needs_support: string;
  g_needs_support: string;

  // Options
  opt_always: string;
  opt_mostly: string;
  opt_rarely: string;
  opt_not_at_all: string;
  opt_fully_understood: string;
  opt_mostly_understood: string;
  opt_struggled: string;
  opt_needed_help: string;
  opt_daily: string;
  opt_4_5_days: string;
  opt_1_3_days: string;
  opt_no_little: string;
  opt_yes_consistently: string;
  opt_most_days: string;
  opt_occasionally: string;
  opt_way_over: string;
  opt_very_consistent: string;
  opt_somewhat_consistent: string;
  opt_inconsistent: string;
  opt_often: string;
  opt_sometimes: string;
  opt_not_observed: string;
  opt_no_doing_well: string;
  opt_yes_discuss: string;
}

export const ENGLISH_CHECKIN_DICTIONARY: CheckinTranslationDictionary = {
  weekly_checkin: "Weekly Check-in",
  weekly_checkin_honesty_note: "Help us support your child’s growth. Please HONESTLY reflect on their routines.",
  please_answer_all: "Please answer all questions.",
  additional_details: "Additional Information (Optional)",
  additional_details_placeholder: "Share any specific observations or concerns...",
  cancel: "Cancel",
  submit: "Submit",
  processing: "Processing...",
  one_submission_per_week: "1/Week",
  submit_weekly_checkin: "Submit Check-in",
  submission_window_closed: "Window Closed",
  submission_window_closed_msg: "Open Thu-Sun.",
  submission_window_info: "Next: Thursday",
  thank_you: "Thank you!",
  submitted_msg: "You've submitted the check-in for this week. The next window opens on Thursday.",
  recent_checkins: "Recent Check-ins",
  no_checkins_yet: "No check-ins submitted yet.",
  week_of: "Week of",
  week_colon: "Week:",
  show_original: "Show Original",
  see_translation: "See Translation",
  translate: "Translate",
  translated_to: "Translated to",
  translating: "Translating...",

  // Questions
  q_study_stress: "1. Academic Balance",
  g_study_stress: "Was your child able to manage their workload without high stress?",
  q_understanding: "2. Conceptual Understanding",
  g_understanding: "Did your child feel confident with what they learned in class?",
  q_physical_activity: "3. Physical Health",
  g_physical_activity: "Did your child get regular physical activity (60–120 mins/day)?",
  q_screen_time: "4. Digital Wellness",
  g_screen_time: "Was non-school screen time kept within healthy limits?",
  q_daily_routine: "5. Routine & Rest",
  g_daily_routine: "Did your child follow a healthy rhythm (sleep, meals, and rest)?",
  q_social_emotional: "6. Social & Emotional Growth",
  g_social_emotional: "Did your child demonstrate positive behaviors?",
  q_needs_support: "7. School Support",
  g_needs_support: "Do you feel your child needs specific support right now?",

  // Options
  opt_always: "Always",
  opt_mostly: "Mostly",
  opt_rarely: "Rarely",
  opt_not_at_all: "Not at all",
  opt_fully_understood: "Fully Understood",
  opt_mostly_understood: "Mostly Understood",
  opt_struggled: "Struggled with some",
  opt_needed_help: "Needed significant help",
  opt_daily: "Daily",
  opt_4_5_days: "4–5 Days",
  opt_1_3_days: "1–3 Days",
  opt_no_little: "No/Very little",
  opt_yes_consistently: "Yes, consistently",
  opt_most_days: "Most days",
  opt_occasionally: "Occasionally exceeded",
  opt_way_over: "Way over limit",
  opt_very_consistent: "Very consistent",
  opt_somewhat_consistent: "Somewhat consistent",
  opt_inconsistent: "Inconsistent/Disrupted",
  opt_often: "Often",
  opt_sometimes: "Sometimes",
  opt_not_observed: "Not observed",
  opt_no_doing_well: "No, they are doing well",
  opt_yes_discuss: "Yes, let’s discuss",
};

// Static dictionaries for high-frequency parent languages
export const STATIC_CHECKIN_TRANSLATIONS: Record<string, Partial<CheckinTranslationDictionary>> = {
  hi: {
    weekly_checkin: "साप्ताहिक चेक-इन",
    weekly_checkin_honesty_note: "अपने बच्चे के विकास में हमारी मदद करें। कृपया उनकी दिनचर्या पर ईमानदारी से विचार करें।",
    please_answer_all: "कृपया सभी प्रश्नों के उत्तर दें।",
    additional_details: "अतिरिक्त जानकारी (वैकल्पिक)",
    additional_details_placeholder: "कोई विशिष्ट अवलोकन या चिंता साझा करें...",
    cancel: "रद्द करें",
    submit: "जमा करें",
    processing: "प्रक्रिया जारी है...",
    one_submission_per_week: "1/सप्ताह",
    submit_weekly_checkin: "चेक-इन जमा करें",
    submission_window_closed: "खिड़की बंद है",
    submission_window_closed_msg: "गुरुवार से रविवार तक खुला रहता है।",
    submission_window_info: "अगला: गुरुवार",
    thank_you: "धन्यवाद!",
    submitted_msg: "आपने इस सप्ताह का चेक-इन जमा कर दिया है। अगली विंडो गुरुवार को खुलेगी।",
    recent_checkins: "हाल के चेक-इन",
    no_checkins_yet: "अभी तक कोई चेक-इन जमा नहीं हुआ है।",
    week_of: "का सप्ताह",
    week_colon: "सप्ताह:",
    show_original: "मूल अंग्रेजी देखें",
    see_translation: "अनुवाद देखें",
    translate: "अनुवाद करें",
    translated_to: "हिंदी में अनूदित",
    translating: "अनुवाद हो रहा है...",

    q_study_stress: "1. अकादमिक संतुलन",
    g_study_stress: "क्या आपका बच्चा बिना अधिक तनाव के अपनी पढ़ाई का भार संभाल पाया?",
    q_understanding: "2. वैचारिक समझ",
    g_understanding: "क्या आपका बच्चा कक्षा में सीखे गए विषयों में आत्मविश्वास महसूस कर रहा था?",
    q_physical_activity: "3. शारीरिक स्वास्थ्य",
    g_physical_activity: "क्या आपके बच्चे को नियमित शारीरिक गतिविधि (60-120 मिनट/दिन) मिली?",
    q_screen_time: "4. डिजिटल स्वास्थ्य",
    g_screen_time: "क्या गैर-शैक्षणिक स्क्रीन समय स्वस्थ सीमा में रहा?",
    q_daily_routine: "5. दिनचर्या और विश्राम",
    g_daily_routine: "क्या आपका बच्चा एक स्वस्थ दिनचर्या (नींद, भोजन और आराम) का पालन कर रहा था?",
    q_social_emotional: "6. सामाजिक और भावनात्मक विकास",
    g_social_emotional: "क्या आपके बच्चे ने सकारात्मक व्यवहार प्रदर्शित किया?",
    q_needs_support: "7. विद्यालय सहायता",
    g_needs_support: "क्या आपको लगता है कि आपके बच्चे को इस समय किसी विशेष सहायता की आवश्यकता है?",

    opt_always: "हमेशा",
    opt_mostly: "अधिकांशतः",
    opt_rarely: "शायद ही कभी",
    opt_not_at_all: "बिल्कुल नहीं",
    opt_fully_understood: "पूरी तरह समझा",
    opt_mostly_understood: "अधिकांश समझ में आया",
    opt_struggled: "कुछ में कठिनाई हुई",
    opt_needed_help: "काफी मदद की आवश्यकता पड़ी",
    opt_daily: "प्रतिदिन",
    opt_4_5_days: "4–5 दिन",
    opt_1_3_days: "1–3 दिन",
    opt_no_little: "नहीं / बहुत कम",
    opt_yes_consistently: "हाँ, लगातार",
    opt_most_days: "अधिकांश दिन",
    opt_occasionally: "कभी-कभी सीमा पार हुई",
    opt_way_over: "सीमा से बहुत अधिक",
    opt_very_consistent: "बहुत सुसंगत",
    opt_somewhat_consistent: "कुछ हद तक सुसंगत",
    opt_inconsistent: "असंगत / बाधित",
    opt_often: "अक्सर",
    opt_sometimes: "कभी-कभी",
    opt_not_observed: "देखा नहीं गया",
    opt_no_doing_well: "नहीं, वे अच्छा कर रहे हैं",
    opt_yes_discuss: "हाँ, इस पर चर्चा करते हैं",
  },
  es: {
    weekly_checkin: "Control Semanal",
    weekly_checkin_honesty_note: "Ayúdenos a apoyar el crecimiento de su hijo. Por favor reflexione con honestidad sobre sus rutinas.",
    please_answer_all: "Por favor responda a todas las preguntas.",
    additional_details: "Información Adicional (Opcional)",
    additional_details_placeholder: "Comparta cualquier observación o inquietud específica...",
    cancel: "Cancelar",
    submit: "Enviar",
    processing: "Procesando...",
    one_submission_per_week: "1/Semana",
    submit_weekly_checkin: "Enviar Control Semanal",
    submission_window_closed: "Ventana Cerrada",
    submission_window_closed_msg: "Abierto de jueves a domingo.",
    submission_window_info: "Próximo: Jueves",
    thank_you: "¡Gracias!",
    submitted_msg: "Ha enviado el control semanal de esta semana. La próxima ventana abre el jueves.",
    recent_checkins: "Controles Recientes",
    no_checkins_yet: "Aún no se han enviado controles.",
    week_of: "Semana del",
    week_colon: "Semana:",
    show_original: "Ver original (Inglés)",
    see_translation: "Ver traducción",
    translate: "Traducir",
    translated_to: "Traducido al español",
    translating: "Traduciendo...",

    q_study_stress: "1. Equilibrio Académico",
    g_study_stress: "¿Pudo su hijo manejar su carga escolar sin estrés excesivo?",
    q_understanding: "2. Comprensión Conceptual",
    g_understanding: "¿Se sintió su hijo seguro con lo aprendido en clase?",
    q_physical_activity: "3. Salud Física",
    g_physical_activity: "¿Realizó su hijo actividad física regular (60–120 min/día)?",
    q_screen_time: "4. Bienestar Digital",
    g_screen_time: "¿Se mantuvo el tiempo de pantalla no escolar dentro de límites saludables?",
    q_daily_routine: "5. Rutina y Descanso",
    g_daily_routine: "¿Siguió su hijo un ritmo saludable (sueño, comidas y descanso)?",
    q_social_emotional: "6. Crecimiento Socioemocional",
    g_social_emotional: "¿Mostró su hijo comportamientos positivos?",
    q_needs_support: "7. Apoyo Escolar",
    g_needs_support: "¿Siente que su hijo necesita apoyo específico en este momento?",

    opt_always: "Siempre",
    opt_mostly: "Casi siempre",
    opt_rarely: "Raras veces",
    opt_not_at_all: "Para nada",
    opt_fully_understood: "Comprendido totalmente",
    opt_mostly_understood: "Comprendido en su mayoría",
    opt_struggled: "Dificultad con algunos temas",
    opt_needed_help: "Necesitó mucha ayuda",
    opt_daily: "A diario",
    opt_4_5_days: "4–5 días",
    opt_1_3_days: "1–3 días",
    opt_no_little: "No / Muy poco",
    opt_yes_consistently: "Sí, de manera constante",
    opt_most_days: "La mayoría de los días",
    opt_occasionally: "Ocasionalmente excedido",
    opt_way_over: "Muy por encima del límite",
    opt_very_consistent: "Muy consistente",
    opt_somewhat_consistent: "Algo consistente",
    opt_inconsistent: "Inconsistente / Interrumpido",
    opt_often: "A menudo",
    opt_sometimes: "A veces",
    opt_not_observed: "No observado",
    opt_no_doing_well: "No, les va muy bien",
    opt_yes_discuss: "Sí, hablemos",
  },
  te: {
    weekly_checkin: "వారపు చెక్-ఇన్",
    weekly_checkin_honesty_note: "మీ పిల్లల ఎదుగుదలకు సహకరించండి. దయచేసి వారి దినచర్యలపై నిజాయితీగా ప్రతిస్పందించండి.",
    please_answer_all: "దయచేసి అన్ని ప్రశ్నలకు సమాధానం ఇవ్వండి.",
    additional_details: "అదనపు సమాచారం (ఐచ్ఛికం)",
    additional_details_placeholder: "ఏదైనా నిర్దిష్ట పరిశీలన లేదా ఆందోళనలను పంచుకోండి...",
    cancel: "రద్దు చేయి",
    submit: "సమర్పించు",
    processing: "ప్రాసెస్ చేస్తోంది...",
    one_submission_per_week: "1/వారం",
    submit_weekly_checkin: "చెక్-ఇన్ సమర్పించండి",
    submission_window_closed: "విండో మూసివేయబడింది",
    submission_window_closed_msg: "గురువారం నుండి ఆదివారం వరకు తెరవబడుతుంది.",
    submission_window_info: "తదుపరి: గురువారం",
    thank_you: "ధన్యవాదాలు!",
    submitted_msg: "మీరు ఈ వారం చెక్-ఇన్‌ను సమర్పించారు. తదుపరి విండో గురువారం తెరవబడుతుంది.",
    recent_checkins: "ఇటీవలి చెక్-ఇన్‌లు",
    no_checkins_yet: "ఇంకా ఎటువంటి చెక్-ఇన్‌లు సమర్పించలేదు.",
    week_of: "వారం తేదీ",
    week_colon: "వారం:",
    show_original: "అసలు ఇంగ్లీష్ చూడండి",
    see_translation: "అనువాదం చూడండి",
    translate: "అనువదించు",
    translated_to: "తెలుగులోకి అనువదించబడింది",
    translating: "అనువదిస్తోంది...",

    q_study_stress: "1. విద్యా సమతుల్యత",
    g_study_stress: "మీ పిల్లలు అధిక ఒత్తిడి లేకుండా వారి చదువును నిర్వహించగలిగారా?",
    q_understanding: "2. భావనాత్మక అవగాహన",
    g_understanding: "తరగతిలో నేర్చుకున్న విషయాలపై మీ పిల్లలు ఆత్మవిశ్వాసంతో ఉన్నారా?",
    q_physical_activity: "3. శారీరక ఆరోగ్యం",
    g_physical_activity: "మీ పిల్లలకు క్రమమైన శారీరక శ్రమ (రోజుకు 60–120 నిమిషాలు) లభించిందా?",
    q_screen_time: "4. డిజిటల్ వెల్‌నెస్",
    g_screen_time: "పాఠశాలేతర స్క్రీన్ సమయం ఆరోగ్యకరమైన పరిమితుల్లో ఉందా?",
    q_daily_routine: "5. దినచర్య మరియు విశ్రాంతి",
    g_daily_routine: "మీ పిల్లలు ఆరోగ్యకరమైన జీవనశైలిని (నిద్ర, భోజనం మరియు విశ్రాంతి) పాటించారా?",
    q_social_emotional: "6. సామాజిక & మానసిక ఎదుగుదల",
    g_social_emotional: "మీ పిల్లలు సానుకూల ప్రవర్తనను ప్రదర్శించారా?",
    q_needs_support: "7. పాఠశాల మద్దతు",
    g_needs_support: "మీ పిల్లలకు ప్రస్తుతం నిర్దిష్ట మద్దతు అవసరమని మీరు భావిస్తున్నారా?",

    opt_always: "ఎల్లప్పుడూ",
    opt_mostly: "చాలా వరకు",
    opt_rarely: "అరుదుగా",
    opt_not_at_all: "అస్సలు లేదు",
    opt_fully_understood: "పూర్తిగా అర్థమైంది",
    opt_mostly_understood: "చాలా వరకు అర్థమైంది",
    opt_struggled: "కొన్నింటిలో ఇబ్బంది పడ్డారు",
    opt_needed_help: "చాలా సహాయం అవసరమైంది",
    opt_daily: "రోజూ",
    opt_4_5_days: "4–5 రోజులు",
    opt_1_3_days: "1–3 రోజులు",
    opt_no_little: "లేదు / చాలా తక్కువ",
    opt_yes_consistently: "అవును, క్రమం తప్పకుండా",
    opt_most_days: "చాలా రోజులు",
    opt_occasionally: "అప్పుడప్పుడు దాటింది",
    opt_way_over: "పరిమితిని మించిపోయింది",
    opt_very_consistent: "చాలా స్థిరంగా",
    opt_somewhat_consistent: "కొంతవరకు స్థిరంగా",
    opt_inconsistent: "అస్థిరంగా / అంతరాయం",
    opt_often: "తరచుగా",
    opt_sometimes: "కొన్నిసార్లు",
    opt_not_observed: "గమనించలేదు",
    opt_no_doing_well: "లేదు, వారు బాగున్నారు",
    opt_yes_discuss: "అవును, చర్చిద్దాం",
  },
  ta: {
    weekly_checkin: "வாராந்திர சரிபார்ப்பு",
    weekly_checkin_honesty_note: "உங்கள் குழந்தையின் வளர்ச்சிக்கு உதவுங்கள். அவர்களின் நடைமுறைகளை நேர்மையாகப் பகிரவும்.",
    please_answer_all: "அனைத்து கேள்விகளுக்கும் பதிலளிக்கவும்.",
    additional_details: "கூடுதல் தகவல் (விருப்பத்தேர்வு)",
    additional_details_placeholder: "குறிப்பிட்ட அவதானிப்புகள் அல்லது கவலைகளைப் பகிரவும்...",
    cancel: "ரத்து செய்",
    submit: "சமர்ப்பி",
    processing: "செயலாக்குகிறது...",
    one_submission_per_week: "1/வாரம்",
    submit_weekly_checkin: "சரிபார்ப்பைச் சமர்ப்பி",
    submission_window_closed: "நேரம் முடிந்தது",
    submission_window_closed_msg: "வியாழன் முதல் ஞாயிறு வரை திறந்திருக்கும்.",
    submission_window_info: "அடுத்தது: வியாழன்",
    thank_you: "நன்றி!",
    submitted_msg: "இந்த வாரத்திற்கான சரிபார்ப்பைச் சமர்ப்பித்துவிட்டீர்கள்.",
    recent_checkins: "சமீபத்திய சரிபார்ப்புகள்",
    no_checkins_yet: "இன்னும் சரிபார்ப்பு எதுவும் சமர்ப்பிக்கப்படவில்லை.",
    week_of: "வாரத் தொடக்கம்",
    week_colon: "வாரம்:",
    show_original: "அசல் ஆங்கிலம்",
    see_translation: "தமிழில் காண்க",
    translate: "மொழிபெயர்",
    translated_to: "தமிழில் மொழிபெயர்க்கப்பட்டது",
    translating: "மொழிபெயர்க்கிறது...",

    q_study_stress: "1. கல்வி சமநிலை",
    g_study_stress: "உங்கள் குழந்தை அதிக மன அழுத்தமின்றி பாடங்களை நிர்வகிக்க முடிந்ததா?",
    q_understanding: "2. கருத்துப் புரிதல்",
    g_understanding: "வகுப்பில் கற்றவற்றில் உங்கள் குழந்தை தன்னம்பிக்கையுடன் இருந்ததா?",
    q_physical_activity: "3. உடற்பயிற்சி & நலம்",
    g_physical_activity: "உங்கள் குழந்தைக்கு வழக்கமான உடற்பயிற்சி (60–120 நிமிடங்கள்/நாள்) கிடைத்ததா?",
    q_screen_time: "4. டிஜிட்டல் நல்வாழ்வு",
    g_screen_time: "பள்ளி சாராத திரை நேரம் ஆரோக்கியமான வரம்பிற்குள் இருந்ததா?",
    q_daily_routine: "5. தினசரி வழக்கம் & ஓய்வு",
    g_daily_routine: "உங்கள் குழந்தை ஆரோக்கியமான வழக்கத்தைப் (தூக்கம், உணவு, ஓய்வு) பின்பற்றியதா?",
    q_social_emotional: "6. சமூக & உணர்ச்சி வளர்ச்சி",
    g_social_emotional: "உங்கள் குழந்தை நேர்மறையான நடத்தைகளை வெளிப்படுத்தியதா?",
    q_needs_support: "7. பள்ளி ஆதரவு",
    g_needs_support: "உங்கள் குழந்தைக்கு இப்போது குறிப்பிட்ட ஆதரவு தேவை என்று நினைக்கிறீர்களா?",

    opt_always: "எப்போதும்",
    opt_mostly: "பெரும்பாலும்",
    opt_rarely: "அரிதாக",
    opt_not_at_all: "இல்லை",
    opt_fully_understood: "முழுமையாகப் புரிந்தது",
    opt_mostly_understood: "பெரும்பாலும் புரிந்தது",
    opt_struggled: "சிலவற்றில் சிரமப்பட்டார்",
    opt_needed_help: "அதிக உதவி தேவைப்பட்டது",
    opt_daily: "தினமும்",
    opt_4_5_days: "4–5 நாட்கள்",
    opt_1_3_days: "1–3 நாட்கள்",
    opt_no_little: "இல்லை / மிகக் குறைவு",
    opt_yes_consistently: "ஆம், சீராக",
    opt_most_days: "பெரும்பாலான நாட்கள்",
    opt_occasionally: "எப்போதாவது தாண்டியது",
    opt_way_over: "வரம்பை மீறியது",
    opt_very_consistent: "மிகவும் சீரானது",
    opt_somewhat_consistent: "ஓரளவு சீரானது",
    opt_inconsistent: "சீரற்றது",
    opt_often: "அடிக்கடி",
    opt_sometimes: "சில நேரங்களில்",
    opt_not_observed: "கவனிக்கப்படவில்லை",
    opt_no_doing_well: "இல்லை, நன்றாக உள்ளனர்",
    opt_yes_discuss: "ஆம், விவாதிப்போம்",
  },
  mr: {
    weekly_checkin: "साप्ताहिक चेक-इन",
    weekly_checkin_honesty_note: "तुमच्या पाल्याच्या विकासात आम्हाला मदत करा. कृपया त्यांच्या दिनचर्येचे प्रामाणिकपणे मूल्यांकन करा.",
    please_answer_all: "कृपया सर्व प्रश्नांची उत्तरे द्या.",
    additional_details: "अतिरिक्त माहिती (पर्यायी)",
    additional_details_placeholder: "काही विशिष्ट निरीक्षणे किंवा चिंता शेअर करा...",
    cancel: "रद्द करा",
    submit: "सादर करा",
    processing: "प्रक्रिया सुरू आहे...",
    one_submission_per_week: "१/आठवडा",
    submit_weekly_checkin: "चेक-इन सादर करा",
    submission_window_closed: "विंडो बंद आहे",
    submission_window_closed_msg: "गुरुवार ते रविवार उघडे असते.",
    submission_window_info: "पुढील: गुरुवार",
    thank_you: "धन्यवाद!",
    submitted_msg: "तुम्ही या आठवड्याचे चेक-इन सादर केले आहे.",
    recent_checkins: "अलीकडील चेक-इन्स",
    no_checkins_yet: "अद्याप कोणतेही चेक-इन सबमिट केलेले नाही.",
    week_of: "चा आठवडा",
    week_colon: "आठवडा:",
    show_original: "मूळ इंग्रजी पहा",
    see_translation: "मराठीत पहा",
    translate: "भाषांतर करा",
    translated_to: "मराठीत अनुवादित",
    translating: "भाषांतर करत आहे...",

    q_study_stress: "१. शैक्षणिक समतोल",
    g_study_stress: "तुमचा पाल्य जास्त तणावाशिवाय अभ्यासाचे नियोजन करू शकला का?",
    q_understanding: "२. संकल्पनांची समज",
    g_understanding: "वर्गात शिकवलेल्या विषयांवर तुमच्या पाल्याचा आत्मविश्वास होता का?",
    q_physical_activity: "३. शारीरिक आरोग्य",
    g_physical_activity: "तुमच्या पाल्याला नियमित शारीरिक हालचाल (६०-१२० मिनिटे/दिवस) मिळाली का?",
    q_screen_time: "४. डिजिटल वेलनेस",
    g_screen_time: "शाळेव्यतिरिक्त स्क्रीन वेळ निरोगी मर्यादेत होता का?",
    q_daily_routine: "५. दिनचर्या आणि विश्रांती",
    g_daily_routine: "तुमच्या पाल्याने योग्य दिनचर्या (झोप, जेवण आणि विश्रांती) पाळली का?",
    q_social_emotional: "६. सामाजिक आणि भावनिक विकास",
    g_social_emotional: "तुमच्या पाल्याने सकारात्मक वर्तन दाखवले का?",
    q_needs_support: "७. शालेय सहाय्य",
    g_needs_support: "तुमच्या पाल्याला सध्या काही विशिष्ट मदतीची गरज आहे असे वाटते का?",

    opt_always: "नेहमी",
    opt_mostly: "बहुतांश वेळा",
    opt_rarely: "क्वचितच",
    opt_not_at_all: "मुळीच नाही",
    opt_fully_understood: "पूर्ण समजले",
    opt_mostly_understood: "बहुतांश समजले",
    opt_struggled: "काही गोष्टींमध्ये अडचण आली",
    opt_needed_help: "खूप मदतीची गरज पडली",
    opt_daily: "दररोज",
    opt_4_5_days: "४–५ दिवस",
    opt_1_3_days: "१–३ दिवस",
    opt_no_little: "नाही / खूप कमी",
    opt_yes_consistently: "होय, सातत्यपूर्ण",
    opt_most_days: "बहुतेक दिवस",
    opt_occasionally: "कधीकधी मर्यादा ओलांडली",
    opt_way_over: "मर्यादेपेक्षा खूप जास्त",
    opt_very_consistent: "खूप सुसंगत",
    opt_somewhat_consistent: "काही प्रमाणात सुसंगत",
    opt_inconsistent: "असंगत / विस्कळीत",
    opt_often: "वारंवार",
    opt_sometimes: "कधीकधी",
    opt_not_observed: "निरीक्षण केले नाही",
    opt_no_doing_well: "नाही, सर्व ठीक आहे",
    opt_yes_discuss: "होय, चर्चा करूया",
  },
  fr: {
    weekly_checkin: "Bilan Hebdomadaire",
    weekly_checkin_honesty_note: "Aidez-nous à accompagner votre enfant. Merci de répondre honnêtement sur ses routines.",
    please_answer_all: "Veuillez répondre à toutes les questions.",
    additional_details: "Informations Complémentaires (Optionnel)",
    additional_details_placeholder: "Partagez vos remarques ou préoccupations...",
    cancel: "Annuler",
    submit: "Soumettre",
    processing: "Traitement en cours...",
    one_submission_per_week: "1/Semaine",
    submit_weekly_checkin: "Soumettre le Bilan",
    submission_window_closed: "Période Clôturée",
    submission_window_closed_msg: "Ouvert du jeudi au dimanche.",
    submission_window_info: "Prochain: Jeudi",
    thank_you: "Merci !",
    submitted_msg: "Vous avez soumis le bilan pour cette semaine.",
    recent_checkins: "Bilans Récents",
    no_checkins_yet: "Aucun bilan soumis pour le moment.",
    week_of: "Semaine du",
    week_colon: "Semaine :",
    show_original: "Voir en anglais",
    see_translation: "Voir la traduction",
    translate: "Traduire",
    translated_to: "Traduit en français",
    translating: "Traduction...",

    q_study_stress: "1. Équilibre Scolaire",
    g_study_stress: "Votre enfant a-t-il géré sa charge de travail sans stress excessif ?",
    q_understanding: "2. Compréhension des Cours",
    g_understanding: "Votre enfant s'est-il senti confiant avec ce qu'il a appris en classe ?",
    q_physical_activity: "3. Santé Physique",
    g_physical_activity: "Votre enfant a-t-il eu une activité physique régulière (60-120 min/jour) ?",
    q_screen_time: "4. Bien-être Numérique",
    g_screen_time: "Le temps d'écran non scolaire a-t-il été maîtrisé ?",
    q_daily_routine: "5. Routine et Sommeil",
    g_daily_routine: "Votre enfant a-t-il suivi un rythme équilibré (sommeil, repas, repos) ?",
    q_social_emotional: "6. Développement Émotionnel",
    g_social_emotional: "Votre enfant a-t-il manifesté des comportements positifs ?",
    q_needs_support: "7. Soutien Scolaire",
    g_needs_support: "Pensez-vous que votre enfant a besoin d'un soutien spécifique en ce moment ?",

    opt_always: "Toujours",
    opt_mostly: "La plupart du temps",
    opt_rarely: "Rarement",
    opt_not_at_all: "Pas du tout",
    opt_fully_understood: "Parfaitement compris",
    opt_mostly_understood: "En grande partie compris",
    opt_struggled: "Difficulté sur certains points",
    opt_needed_help: "A eu besoin de beaucoup d'aide",
    opt_daily: "Quotidien",
    opt_4_5_days: "4–5 jours",
    opt_1_3_days: "1–3 jours",
    opt_no_little: "Non / Très peu",
    opt_yes_consistently: "Oui, régulièrement",
    opt_most_days: "La plupart des jours",
    opt_occasionally: "Parfois dépassé",
    opt_way_over: "Très au-delà de la limite",
    opt_very_consistent: "Très régulier",
    opt_somewhat_consistent: "Assez régulier",
    opt_inconsistent: "Irrégulier / Perturbé",
    opt_often: "Souvent",
    opt_sometimes: "Parfois",
    opt_not_observed: "Non observé",
    opt_no_doing_well: "Non, tout va bien",
    opt_yes_discuss: "Oui, discutons-en",
  },
  ar: {
    weekly_checkin: "تسجيل المتابعة الأسبوعي",
    weekly_checkin_honesty_note: "ساعدنا في دعم نمو طفلك. يرجى التعبير بصدق عن روتينه الأسبوعي.",
    please_answer_all: "يرجى الإجابة على جميع الأسئلة.",
    additional_details: "معلومات إضافية (اختياري)",
    additional_details_placeholder: "شارك أي ملاحظات أو مخاوف محددة...",
    cancel: "إلغاء",
    submit: "إرسال",
    processing: "جاري المعالجة...",
    one_submission_per_week: "مرة/أسبوع",
    submit_weekly_checkin: "إرسال المتابعة",
    submission_window_closed: "النافذة مغلقة",
    submission_window_closed_msg: "متاح من الخميس إلى الأحد.",
    submission_window_info: "القادم: الخميس",
    thank_you: "شكراً لك!",
    submitted_msg: "تم إرسال المتابعة لهذا الأسبوع.",
    recent_checkins: "المتابعات السابقة",
    no_checkins_yet: "لم يتم إرسال أي متابعة بعد.",
    week_of: "أسبوع",
    week_colon: "الأسبوع:",
    show_original: "عرض النص الأصلي",
    see_translation: "عرض الترجمة",
    translate: "ترجمة",
    translated_to: "مترجم إلى العربية",
    translating: "جاري الترجمة...",

    q_study_stress: "1. التوازن الأكاديمي",
    g_study_stress: "هل تمكن طفلك من إدارة واجباته المدرسية دون ضغوط عالية؟",
    q_understanding: "2. الفهم والاستيعاب",
    g_understanding: "هل شعر طفلك بالثقة تجاه ما تعلمه في الفصل؟",
    q_physical_activity: "3. النشاط البدني",
    g_physical_activity: "هل مارس طفلك نشاطاً بدنياً بانتظام (60-120 دقيقة/يوم)؟",
    q_screen_time: "4. استخدام الشاشات",
    g_screen_time: "هل كان وقت استخدام الشاشات غير الدراسية ضمن الحدود الصحية؟",
    q_daily_routine: "5. الروتين والراحة",
    g_daily_routine: "هل اتبع طفلك روتيناً صحياً (النوم، الوجبات، والراحة)؟",
    q_social_emotional: "6. النمو الاجتماعي والعاطفي",
    g_social_emotional: "هل أظهر طفلك سلوكيات إيجابية؟",
    q_needs_support: "7. الدعم المدرسي",
    g_needs_support: "هل تشعر أن طفلك يحتاج إلى دعم خاص في الوقت الحالي؟",

    opt_always: "دائماً",
    opt_mostly: "في معظم الأحيان",
    opt_rarely: "نادراً",
    opt_not_at_all: "إطلاقاً",
    opt_fully_understood: "مفهوم تماماً",
    opt_mostly_understood: "مفهوم في الغالب",
    opt_struggled: "واجه صعوبة في البعض",
    opt_needed_help: "احتاج إلى مساعدة كبيرة",
    opt_daily: "يومياً",
    opt_4_5_days: "4–5 أيام",
    opt_1_3_days: "1–3 أيام",
    opt_no_little: "لا / قليل جداً",
    opt_yes_consistently: "نعم، باستمرار",
    opt_most_days: "معظم الأيام",
    opt_occasionally: "تجاوز الحد أحياناً",
    opt_way_over: "تجاوز كبير للحد",
    opt_very_consistent: "منتظم جداً",
    opt_somewhat_consistent: "منتظم إلى حد ما",
    opt_inconsistent: "غير منتظم",
    opt_often: "غالباً",
    opt_sometimes: "أحياناً",
    opt_not_observed: "لم تتم ملاحظته",
    opt_no_doing_well: "لا، أمورهم ممتازة",
    opt_yes_discuss: "نعم، نود المناقشة",
  }
};

// Mapping from option keys to English canonical values
export const OPTION_KEY_TO_ENGLISH: Record<string, string> = {
  opt_always: "Always",
  opt_mostly: "Mostly",
  opt_rarely: "Rarely",
  opt_not_at_all: "Not at all",
  opt_fully_understood: "Fully Understood",
  opt_mostly_understood: "Mostly Understood",
  opt_struggled: "Struggled with some",
  opt_needed_help: "Needed significant help",
  opt_daily: "Daily",
  opt_4_5_days: "4–5 Days",
  opt_1_3_days: "1–3 Days",
  opt_no_little: "No/Very little",
  opt_yes_consistently: "Yes, consistently",
  opt_most_days: "Most days",
  opt_occasionally: "Occasionally exceeded",
  opt_way_over: "Way over limit",
  opt_very_consistent: "Very consistent",
  opt_somewhat_consistent: "Somewhat consistent",
  opt_inconsistent: "Inconsistent/Disrupted",
  opt_often: "Often",
  opt_sometimes: "Sometimes",
  opt_not_observed: "Not observed",
  opt_no_doing_well: "No, they are doing well",
  opt_yes_discuss: "Yes, let’s discuss",
};

// Reverse map: English text to option key
export const ENGLISH_TO_OPTION_KEY: Record<string, string> = Object.entries(OPTION_KEY_TO_ENGLISH).reduce(
  (acc, [k, v]) => {
    acc[v.toLowerCase().trim()] = k;
    return acc;
  },
  {} as Record<string, string>
);

// Dynamic In-Memory Cache for Gemini Translations
const dynamicTranslationCache: Record<string, CheckinTranslationDictionary> = {};

/**
 * Fetch dynamic AI translation for any of the 60+ languages supported by the app
 */
export const fetchAICheckinTranslation = async (langCode: string): Promise<CheckinTranslationDictionary> => {
  if (!langCode || langCode === 'en') return ENGLISH_CHECKIN_DICTIONARY;

  // 1. Static Dictionary check
  if (STATIC_CHECKIN_TRANSLATIONS[langCode]) {
    return {
      ...ENGLISH_CHECKIN_DICTIONARY,
      ...STATIC_CHECKIN_TRANSLATIONS[langCode],
    };
  }

  // 2. In-memory cache check
  if (dynamicTranslationCache[langCode]) {
    return dynamicTranslationCache[langCode];
  }

  // 3. LocalStorage cache check
  try {
    const cached = localStorage.getItem(`edugravity_checkin_trans_${langCode}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      dynamicTranslationCache[langCode] = { ...ENGLISH_CHECKIN_DICTIONARY, ...parsed };
      return dynamicTranslationCache[langCode];
    }
  } catch (e) {
    // ignore
  }

  // 4. Request Gemini translation in one efficient structured call
  const targetLangLabel = LANGUAGES.find(l => l.code === langCode)?.label || langCode;

  try {
    const prompt = `You are an educational translator. Translate all string values in the following JSON object into ${targetLangLabel}. 
Keep the exact same keys. Return ONLY the valid JSON object without any markdown wrapping or backticks.

${JSON.stringify(ENGLISH_CHECKIN_DICTIONARY, null, 2)}`;

    const response = await generateContentWithRetry({
      model: 'gemini-3-flash-preview',
      contents: prompt,
      config: { systemInstruction: SYSTEM_INSTRUCTION_TRANSLATOR }
    });

    let rawText = (response.text || '').trim();
    if (rawText.startsWith('```')) {
      rawText = rawText.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '').trim();
    }

    const translatedObj = JSON.parse(rawText);
    const fullDict: CheckinTranslationDictionary = {
      ...ENGLISH_CHECKIN_DICTIONARY,
      ...translatedObj,
      translated_to: `Translated to ${targetLangLabel}`
    };

    dynamicTranslationCache[langCode] = fullDict;
    try {
      localStorage.setItem(`edugravity_checkin_trans_${langCode}`, JSON.stringify(fullDict));
    } catch (e) {
      // ignore quota
    }

    return fullDict;
  } catch (err) {
    console.warn("Dynamic check-in translation failed, using English fallback:", err);
    return ENGLISH_CHECKIN_DICTIONARY;
  }
};

/**
 * Synchronous dictionary resolver:
 * Gives immediate static/cached translation, avoiding layout flashes
 */
export const getCheckinDictionarySync = (langCode: string): CheckinTranslationDictionary => {
  if (!langCode || langCode === 'en') return ENGLISH_CHECKIN_DICTIONARY;
  if (STATIC_CHECKIN_TRANSLATIONS[langCode]) {
    return { ...ENGLISH_CHECKIN_DICTIONARY, ...STATIC_CHECKIN_TRANSLATIONS[langCode] };
  }
  if (dynamicTranslationCache[langCode]) {
    return dynamicTranslationCache[langCode];
  }
  try {
    const cached = localStorage.getItem(`edugravity_checkin_trans_${langCode}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      dynamicTranslationCache[langCode] = { ...ENGLISH_CHECKIN_DICTIONARY, ...parsed };
      return dynamicTranslationCache[langCode];
    }
  } catch (e) {}
  return ENGLISH_CHECKIN_DICTIONARY;
};
